import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import MetaHead from "./MetaHead";
import { useTranslation } from 'react-i18next';
import { trackPageVisit } from '../utils/analytics';
import Footer from "./Footer";
import SocialHandles from "./SocialHandles";

const TermsAndConditions: React.FC = () => {
  const { t } = useTranslation();

  useEffect(() => {
    
    trackPageVisit('terms');
  }, []);

  return (
    <>
      <MetaHead
        title={t('terms.title')}
        description={t('terms.description') as string}
        keywords={t('terms.keywords') as string}
      />
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-accent-50 dark:from-neutral-900 dark:via-neutral-800 dark:to-neutral-900 animate-fade-in">
      {/* Navbar */}
      <nav className="bg-white/80 dark:bg-neutral-900/80 backdrop-blur-sm border-b border-neutral-200 dark:border-neutral-700 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl flex items-center justify-center">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <span className="text-xl font-bold bg-gradient-to-r from-primary-600 to-accent-600 bg-clip-text text-transparent">
                Streak Spark
              </span>
            </Link>
            <div className="flex items-center space-x-4">
              <Link
                to="/"
                className="text-neutral-600 dark:text-neutral-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors duration-300"
              >
                {t('terms.home')}
              </Link>
              <Link
                to="/app"
                className="bg-gradient-to-r from-primary-600 to-primary-700 text-white px-4 py-2 rounded-xl font-semibold hover:shadow-glow transition-all duration-300 hover:-translate-y-0.5"
              >
                {t('terms.openApp')}
              </Link>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-6xl font-bold bg-gradient-to-r from-primary-600 via-accent-600 to-secondary-600 bg-clip-text text-transparent mb-4 leading-tight">
            {t('terms.pageTitle')}
          </h1>
          <p className="text-lg text-neutral-600 dark:text-neutral-300">
            {t('terms.lastUpdated')}: {new Date().toLocaleDateString()}
          </p>
        </div>

        {/* Content */}
        <div className="glass-card p-8 rounded-2xl space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100 mb-4">{t('terms.acceptanceOfTerms')}</h2>
            <p className="text-neutral-600 dark:text-neutral-300 leading-relaxed">
              {t('terms.acceptanceText')}
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100 mb-4">{t('terms.descriptionOfService')}</h2>
            <p className="text-neutral-600 dark:text-neutral-300 leading-relaxed">
              {t('terms.serviceDescription')}
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100 mb-4">{t('terms.userAccounts')}</h2>
            <div className="space-y-4">
              <p className="text-neutral-600 dark:text-neutral-300 leading-relaxed">
                {t('terms.userAccountsText1')}
              </p>
              <p className="text-neutral-600 dark:text-neutral-300 leading-relaxed">
                {t('terms.userAccountsText2')}
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100 mb-4">{t('terms.userConduct')}</h2>
            <p className="text-neutral-600 dark:text-neutral-300 leading-relaxed mb-4">{t('terms.userConductIntro')}</p>
            <ul className="list-disc list-inside text-neutral-600 dark:text-neutral-300 leading-relaxed space-y-2">
              {(t('terms.conductList', { returnObjects: true }) as string[]).map((item: string, index: number) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100 mb-4">{t('terms.dataPrivacy')}</h2>
            <div className="space-y-4">
              <p className="text-neutral-600 dark:text-neutral-300 leading-relaxed">
                <strong>{t('terms.dataStorage')}</strong> {t('terms.dataStorageText')}
              </p>
              <p className="text-neutral-600 dark:text-neutral-300 leading-relaxed">
                <strong>{t('terms.sessionManagement')}</strong> {t('terms.sessionManagementText')}
              </p>
              <p className="text-neutral-600 dark:text-neutral-300 leading-relaxed">
                {t('terms.privacyNote')}
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100 mb-4">{t('terms.intellectualProperty')}</h2>
            <p className="text-neutral-600 dark:text-neutral-300 leading-relaxed">
              {t('terms.ipText')}
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100 mb-4">{t('terms.limitationOfLiability')}</h2>
            <p className="text-neutral-600 dark:text-neutral-300 leading-relaxed">
              {t('terms.liabilityText')}
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100 mb-4">{t('terms.termination')}</h2>
            <p className="text-neutral-600 dark:text-neutral-300 leading-relaxed">
              {t('terms.terminationText')}
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100 mb-4">{t('terms.changesToTerms')}</h2>
            <p className="text-neutral-600 dark:text-neutral-300 leading-relaxed">
              {t('terms.changesText')}
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100 mb-4">{t('terms.contactInformation')}</h2>
            <p className="text-neutral-600 dark:text-neutral-300 leading-relaxed">
              {t('terms.contactText')}
            </p>
          </section>
        </div>

      </div>
      <SocialHandles />
      <Footer />
    </div>
    </>
  );
};

export default TermsAndConditions;
